import React, { useEffect, useMemo, useRef, useState } from "react";
import { BRICK_TYPES, colorForCell } from "../brickTypes";
import { generateCaveGrid } from "../levelGenerator";
import "./LevelEditor.css";

// The game has no camera scroll/zoom (see lib/game/neon_breaker_game.dart
// _buildLevel: cellSize = worldSize.x / cols) - the whole grid must fit one
// fixed screen width, so brick size is purely a function of column count.
// 390px is a representative modern phone's logical width (iPhone 14/15/16
// standard size); used only to estimate how small bricks will render, not
// as a hard device assumption.
const REFERENCE_SCREEN_WIDTH = 390;
const MAX_COLS = 30;
const MAX_ROWS = 40;

function estimateCellSize(cols) {
  return REFERENCE_SCREEN_WIDTH / cols;
}

function cellSizeHint(px) {
  if (px >= 14) return { level: "ok", label: `~${px.toFixed(1)}px/ô — rõ ràng` };
  if (px >= 8) return { level: "warn", label: `~${px.toFixed(1)}px/ô — khá dày, nên test trên máy thật` };
  return { level: "danger", label: `~${px.toFixed(1)}px/ô — quá nhỏ, khó thấy trên điện thoại` };
}

function buildGrid(rows, cols, previous) {
  const grid = [];
  for (let r = 0; r < rows; r++) {
    const row = [];
    for (let c = 0; c < cols; c++) {
      row.push(previous?.[r]?.[c] ?? 0);
    }
    grid.push(row);
  }
  return grid;
}

const LevelEditor = ({ initialData, existingIds, onSave, onCancel, onDelete }) => {
  const [id, setId] = useState(initialData.id);
  const [name, setName] = useState(initialData.name);
  const [rows, setRows] = useState(initialData.rows);
  const [cols, setCols] = useState(initialData.cols);
  const [dropRate, setDropRate] = useState(initialData.dropRate);
  const [grid, setGrid] = useState(() => buildGrid(initialData.rows, initialData.cols, initialData.grid));
  const [selectedTool, setSelectedTool] = useState(1);
  const [error, setError] = useState("");

  const isNew = initialData.isNew;

  const normalBrickCount = useMemo(
    () => grid.reduce((sum, row) => sum + row.filter((v) => v !== 0 && v !== 2).length, 0),
    [grid]
  );

  const handleResize = (nextRows, nextCols) => {
    const boundedRows = Math.max(1, Math.min(MAX_ROWS, nextRows));
    const boundedCols = Math.max(1, Math.min(MAX_COLS, nextCols));
    setRows(boundedRows);
    setCols(boundedCols);
    setGrid((prev) => buildGrid(boundedRows, boundedCols, prev));
  };

  const cellHint = cellSizeHint(estimateCellSize(cols));

  const paintCell = (r, c, tool = selectedTool) => {
    setGrid((prev) => {
      if (prev[r][c] === tool) return prev;
      const next = prev.map((row) => row.slice());
      next[r][c] = tool;
      return next;
    });
  };

  // Drag-to-paint: mousedown/touchstart on a cell starts painting, then
  // every cell the pointer passes over while held gets painted too - so a
  // whole wall of bricks is one drag instead of one click per cell.
  const isPaintingRef = useRef(false);
  const paintToolRef = useRef(selectedTool);

  useEffect(() => {
    const stopPainting = () => {
      isPaintingRef.current = false;
    };
    window.addEventListener("pointerup", stopPainting);
    window.addEventListener("pointercancel", stopPainting);
    return () => {
      window.removeEventListener("pointerup", stopPainting);
      window.removeEventListener("pointercancel", stopPainting);
    };
  }, []);

  const startPainting = (e, r, c) => {
    // Without releasing implicit pointer capture, every subsequent
    // pointerenter during the drag would still target this first cell
    // instead of the ones the pointer actually moves over.
    e.currentTarget.releasePointerCapture(e.pointerId);
    isPaintingRef.current = true;
    paintToolRef.current = selectedTool;
    paintCell(r, c, selectedTool);
  };

  const continuePainting = (r, c) => {
    if (!isPaintingRef.current) return;
    paintCell(r, c, paintToolRef.current);
  };

  const [density, setDensity] = useState(0.6);

  const handleAutoGenerate = () => {
    const generated = generateCaveGrid({ rows, cols, brickFillChance: density });
    setGrid(generated);
  };

  const handleSave = () => {
    const numericId = Number(id);
    if (!numericId || numericId <= 0) {
      setError("Level id must be a positive number.");
      return;
    }
    if (isNew && existingIds.includes(numericId)) {
      setError(`Level id ${numericId} already exists.`);
      return;
    }
    if (!name.trim()) {
      setError("Level name is required.");
      return;
    }
    setError("");
    onSave({
      id: numericId,
      name: name.trim(),
      rows,
      cols,
      dropRate: Number(dropRate),
      grid,
    });
  };

  return (
    <div className="level-editor">
      <div className="level-editor-form card">
        <h2>{isNew ? "New Level" : `Edit Level ${initialData.id}`}</h2>

        <div className="form-row">
          <label>
            ID
            <input type="number" value={id} disabled={!isNew} onChange={(e) => setId(e.target.value)} />
          </label>
          <label>
            Name
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
        </div>

        <div className="form-row">
          <label>
            Rows
            <input
              type="number"
              value={rows}
              onChange={(e) => handleResize(Number(e.target.value) || 1, cols)}
            />
          </label>
          <label>
            Cols
            <input
              type="number"
              value={cols}
              onChange={(e) => handleResize(rows, Number(e.target.value) || 1)}
            />
            <span className={`cell-size-hint cell-size-hint-${cellHint.level}`}>{cellHint.label}</span>
          </label>
          <label>
            Drop rate
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={dropRate}
              onChange={(e) => setDropRate(e.target.value)}
            />
          </label>
        </div>

        <div className="tool-palette">
          {BRICK_TYPES.map((t) => (
            <button
              key={t.code}
              type="button"
              className={`tool-swatch ${selectedTool === t.code ? "selected" : ""}`}
              style={{ backgroundColor: colorForCell(0, t.code) ?? "transparent" }}
              onClick={() => setSelectedTool(t.code)}
              title={t.label}
            >
              {t.key === "empty" ? "✕" : ""}
            </button>
          ))}
          <span className="tool-label">{BRICK_TYPES.find((t) => t.code === selectedTool)?.label}</span>
        </div>

        <div className="auto-generate-row">
          <label className="density-label">
            Độ đặc: {Math.round(density * 100)}%
            <input
              type="range"
              min="0.3"
              max="0.85"
              step="0.05"
              value={density}
              onChange={(e) => setDensity(Number(e.target.value))}
            />
          </label>
          <button type="button" className="btn" onClick={handleAutoGenerate}>
            🎲 Tự động tạo
          </button>
        </div>
        <p className="auto-generate-hint">
          Sinh dạng "hang động" ngẫu nhiên (giống thuật toán procedural của game) - luôn đảm bảo
          mọi gạch đều có đường bóng chạm tới. Bấm lại để đổi bố cục khác, rồi chỉnh tay theo ý muốn.
        </p>

        <p className="brick-count">Destructible bricks: {normalBrickCount}</p>

        {error && <p style={{ color: "var(--danger-color)" }}>{error}</p>}

        <div className="form-actions">
          <button className="btn btn-primary" onClick={handleSave}>
            Save
          </button>
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          {!isNew && onDelete && (
            <button className="btn btn-danger" onClick={() => onDelete(initialData.id)}>
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="level-editor-grid card">
        <p className="drag-hint">Nhấn giữ và kéo qua nhiều ô để sơn nhanh.</p>
        <div
          className="brick-grid"
          style={{ gridTemplateColumns: `repeat(${cols}, 28px)` }}
        >
          {grid.map((row, r) =>
            row.map((code, c) => {
              const color = colorForCell(r, code);
              return (
                <button
                  key={`${r}-${c}`}
                  type="button"
                  className="brick-cell"
                  style={{ backgroundColor: color ?? "transparent" }}
                  onPointerDown={(e) => startPainting(e, r, c)}
                  onPointerEnter={() => continuePainting(r, c)}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default LevelEditor;
